/**
 * Design System v3 — configuración de navegación (árbol del sidebar).
 *
 * Cada entrada de `DS_NAV` es o bien una **carpeta** (`kind: 'folder'`, colapsable,
 * con páginas hijas) o una **página suelta** (`kind: 'page'`, link directo de nivel
 * superior, sin hijos — ej. "Getting Started"). Todo el sidebar y el redirect inicial
 * se derivan de acá. Sumar páginas es agregar items + su `<Route>` en `App.tsx`.
 */

export const DS_BASE_PATH = '/design-system';

export interface DSPage {
  /** Slug del último segmento de la URL, ej. 'typography'. */
  slug: string;
  /** Texto visible en el sidebar. */
  label: string;
}

export interface DSFolder {
  kind: 'folder';
  /** Slug del segmento de carpeta en la URL, ej. 'foundations'. */
  slug: string;
  label: string;
  /** Páginas hoja dentro de la carpeta (vacío = carpeta sin páginas todavía). */
  pages: DSPage[];
}

export interface DSStandalonePage {
  kind: 'page';
  /** Slug relativo a `DS_BASE_PATH` → `/design-system/<slug>`. */
  slug: string;
  label: string;
}

export type DSNavItem = DSFolder | DSStandalonePage;

export const DS_NAV: DSNavItem[] = [
  // Página de bienvenida — landing del DS.
  { kind: 'page', slug: 'getting-started', label: 'Getting Started' },
  {
    kind: 'folder',
    slug: 'foundations',
    label: 'Foundations',
    pages: [
      { slug: 'typography', label: 'Typography' },
      { slug: 'colors', label: 'Colors' },
      { slug: 'spacing', label: 'Spacing' },
      { slug: 'shadows', label: 'Shadows' },
      { slug: 'radius', label: 'Radius' },
    ],
  },
  {
    kind: 'folder',
    slug: 'tokens',
    label: 'Tokens',
    pages: [{ slug: 'colors', label: 'Semantic Colors' }],
  },
  {
    kind: 'folder',
    slug: 'components',
    label: 'Components',
    pages: [{ slug: 'icons', label: 'Icons' }],
  },
];

/** Ruta absoluta de una página dentro de una carpeta. */
export function dsPagePath(folderSlug: string, pageSlug: string): string {
  return `${DS_BASE_PATH}/${folderSlug}/${pageSlug}`;
}

/** Ruta absoluta de una página suelta de nivel superior. */
export function dsStandalonePath(slug: string): string {
  return `${DS_BASE_PATH}/${slug}`;
}

/** Destino del index redirect: la primera página del árbol (Getting Started). */
export function dsFirstPagePath(): string {
  for (const item of DS_NAV) {
    if (item.kind === 'page') return dsStandalonePath(item.slug);
    if (item.pages.length > 0) return dsPagePath(item.slug, item.pages[0].slug);
  }
  return DS_BASE_PATH;
}
